import { useCallback, useState } from 'react';

import { User, useLogInUser } from '@givebacks/api/core';
import axios, { AxiosError } from 'axios';
import { ResetPasswordModal } from 'core/Components';
import { getErrorMessage_v2 } from 'core/Utils';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  notifications,
} from 'ui';
import { isEmail, isNotEmpty, useForm } from 'ui/Form';

import { useSendEmailConfirmation } from '../../../hooks/queries/Core/users';

type Props = {
  setIsSignIn: (isSignIn: boolean) => void;
  onSuccessfulSignIn: (user: User) => void;
};

const SignInForm = ({ setIsSignIn, onSuccessfulSignIn }: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [showEmailReset, setShowEmailReset] = useState<boolean>(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [searchParams] = useSearchParams();

  const { mutate: sendEmailConfirmation } = useSendEmailConfirmation();
  const { mutate: logInUser, isPending: isLoading } = useLogInUser();

  const form = useForm({
    initialValues: {
      email: searchParams.get('email') || '',
      password: '',
    },
    validate: {
      email: isEmail('Invalid Email'),
      password: isNotEmpty('Invalid Password'),
    },
  });

  const handleSignIn = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setErrorMessage('');
      setShowEmailReset(false);
      if (!form.validate().hasErrors) {
        const request = {
          user: {
            email: form.values.email,
            password: form.values.password,
          },
        };
        logInUser(request, {
          onError: (e) => {
            const error = e as Error | AxiosError;
            if (axios.isAxiosError(error)) {
              if (
                error?.response?.data.error.toLowerCase() === 'user has not confirmed their email'
              ) {
                setShowEmailReset(true);
              }
              setErrorMessage(getErrorMessage_v2(e));
            }
          },
          onSuccess: (user) => {
            onSuccessfulSignIn(user);
          },
        });
      }
    },
    [form, logInUser, onSuccessfulSignIn]
  );

  const handleEmailConfirmation = useCallback(() => {
    sendEmailConfirmation(form.values.email, {
      onSuccess: () => {
        notifications.show({
          message: 'Email confirmation resent',
          variant: 'success',
        });
      },
      onError: () => {
        notifications.show({
          message: 'Error sending email confirmation',
          variant: 'error',
        });
      },
    });
  }, [sendEmailConfirmation, form.values.email]);

  return (
    <Box>
      <form>
        <Stack justify="space-between">
          <Stack gap="xs" mih={268}>
            <TextInput placeholder="Email Address" {...form.getInputProps('email')} />
            <PasswordInput placeholder="Password" {...form.getInputProps('password')} />
            <Group justify="flex-end">
              <Button variant="subtle" size="sm" onClick={() => setIsResetModalOpen(true)}>
                Forgot password?
              </Button>
            </Group>
            <Alert.Collapse in={!!errorMessage} title={errorMessage} type="error" />
            {showEmailReset && (
              <Button variant="subtle" onClick={handleEmailConfirmation}>
                Resend Email Confirmation
              </Button>
            )}
          </Stack>
          <Stack>
            <Button
              variant="gradient"
              onClick={handleSignIn}
              fullWidth
              loading={isLoading}
              type="submit"
            >
              Sign In
            </Button>
            <Group justify="center" gap={0}>
              <Text fz="sm" c="gray">
                Don&apos;t have an account?
              </Text>
              <Button
                variant="transparent"
                size="compact-sm"
                px="xs"
                onClick={() => setIsSignIn(false)}
              >
                Create an Account
              </Button>
            </Group>
          </Stack>
        </Stack>
      </form>
      <ResetPasswordModal opened={isResetModalOpen} onClose={() => setIsResetModalOpen(false)} />
    </Box>
  );
};

export { SignInForm };
